import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { KundesegmentEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/kundesegment-enum';
import { ServiceintervalDto } from '../../../../../api/models/OK/Egis/Service/Models/DTO/serviceinterval-dto';
import { EftersynStateEnum } from '../../../../../api/models/OK/Egis/Service/Models/DTO/Enums/eftersyn-state-enum';
import { PriceService } from 'src/app/api/services';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';

@Component({
    selector: 'app-slutopgoerelse',
    templateUrl: './slutopgoerelse.component.html',
    styleUrls: ['./slutopgoerelse.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SlutopgoerelseComponent implements OnInit {
  momsSuffix: string;
  _currentServiceinterval: ServiceintervalDto;
  frieTilkaldCorrectionAmount: number;
  tilkaldYearlyPrice: number;
  tilkaldPeriodPrice: number;
  unsubscribe$ = new Subject<void>();
  priceError: string;

  @Input() form: UntypedFormGroup;

  _kundeSegment: KundesegmentEnum;
  @Input() set kundeSegment(value: KundesegmentEnum) {
    this.momsSuffix = value === KundesegmentEnum.Privat ? '(inkl. moms)' : '(ekskl. moms)';
    this._kundeSegment = value;
  }
  @Input() set currentServiceinterval(value: ServiceintervalDto) {
    this._currentServiceinterval = value;
    this.currentServiceintervalUpdated();
  }
  @Input() isWithFrieTilkald: boolean = false;
  @Input() serviceaftaleId: number;
  @Input() eftersynsInterval: number;

  private _endDate: Date;
  @Input() set endDate(value: Date) {
    this._endDate = value;
    this.updatePriceFrieTilkaldCorrection();
  }

  constructor(
    private priceService: PriceService,
    private errorHandler: ErrorHandlerService,
  ) {}

  get createSlutopgoerelse() {
    return this.form.get('createSlutopgoerelse');
  }

  get addManuelCorrection(): boolean {
    return this.form.get('addManuelCorrection')?.value;
  }

  get payUntilPeriodEnd() {
    return this.form.get('payUntilEndOfPeriod');
  }

  ngOnInit(): void {
    if (this.isWithFrieTilkald) {
      this.updatePriceFrieTilkaldCorrection();
      this.payUntilPeriodEnd.valueChanges
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((val) => this.updateFrieTilKaldCorrectionAmount(val));
    }
  }

  private updateFrieTilKaldCorrectionAmount(payUntilEnd: boolean) {
    let yearsPerServiceInterval = this.eftersynsInterval / 12;
    this.frieTilkaldCorrectionAmount = this.tilkaldPeriodPrice;
    if (payUntilEnd) {
      this.frieTilkaldCorrectionAmount = this.frieTilkaldCorrectionAmount - yearsPerServiceInterval * this.tilkaldYearlyPrice;
    }
  }

  private updatePriceFrieTilkaldCorrection() {
    (forkJoin([
      this.priceService.getPriceForFrieTilkald({
        caller: EGIS_SERVICE_CALLER,
        ServiceaftaleId: this.serviceaftaleId,
      }),
      this.priceService.getPeriodPriceForFrieTilkald({
        caller: EGIS_SERVICE_CALLER,
        ServiceaftaleId: this.serviceaftaleId,
        calculationDate: this._endDate?.toOkDateString(),
      }),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((results) => {
        this.tilkaldYearlyPrice = this._kundeSegment == KundesegmentEnum.Privat ? results[0].withMoms : results[0].withoutMoms;
        this.tilkaldPeriodPrice = this._kundeSegment == KundesegmentEnum.Privat ? results[1].withMoms : results[1].withoutMoms;
        this.updateFrieTilKaldCorrectionAmount(this.payUntilPeriodEnd.value);
      }),
      (error: HttpErrorResponse) => {
        this.handlePriceError(error);
      });
  }

  private currentServiceintervalUpdated() {
    const payUntilPeriodEndControl = this.payUntilPeriodEnd;
    if (!payUntilPeriodEndControl) {
      return;
    }

    if (payUntilPeriodEndControl.value != null) {
      return;
    }

    const payUntilEndOfPeriod =
      this._currentServiceinterval != null &&
      this._currentServiceinterval.eftersynState != null &&
      this._currentServiceinterval.eftersynState.eftersynState === EftersynStateEnum.Completed;
    payUntilPeriodEndControl.setValue(payUntilEndOfPeriod);
  }

  handlePriceError(error: HttpErrorResponse) {
    if (error && error.status === 400) {
      const errorText = this.errorHandler.getValidationErrorText(error);
      this.priceError = errorText;
    }
  }
}
